import { useTranslation } from 'react-i18next'
import { CurecallTag } from '@honestica/curecall-ui'
import { type TChatbotNode } from '../node.type'
import { EHumanReviewRequestProblem } from 'src/pages/dashboard/_components/human-review-requests.type'
import { useProjectMedAlerts } from '../chatbot-form/use-project-med-alerts.hook'
import { useProblemLabels } from 'src/pages/dashboard/_components/use-problem-labels'
import { MED_ALERT_UI_TO_TAG_STATUS } from 'src/types/med-alert'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { EVariableType } from 'src/types/variable'
import { useLocale } from 'src/hooks/use-locale'

interface NodeAddedActionsProps {
  currentNode: TChatbotNode | undefined
  conditionKey: string
}

export function NodeAddedActions({ currentNode, conditionKey }: NodeAddedActionsProps) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { data: medAlerts = [] } = useProjectMedAlerts()
  const problemLabels = useProblemLabels()

  const variable = currentNode?.data.variable

  let medAlertId: number | undefined
  let requestHumanReviewProblem: EHumanReviewRequestProblem | undefined

  if (variable?.type === EVariableType.BOOLEAN) {
    const booleanCondition = variable.condition
    if (conditionKey === 'yes' || conditionKey === 'no') {
      medAlertId = booleanCondition?.[conditionKey]?.medAlertId
      requestHumanReviewProblem = booleanCondition?.[conditionKey]?.requestHumanReviewProblem
    }
  } else if (variable?.type === EVariableType.INTEGER_VALUE_RANGE) {
    const rangeMatch = conditionKey.match(/^range-(\d+)$/)
    if (rangeMatch) {
      const rangeIndex = parseInt(rangeMatch[1]!, 10)
      const rangeCondition = variable.condition?.ranges[rangeIndex]
      medAlertId = rangeCondition?.action?.medAlertId
      requestHumanReviewProblem = rangeCondition?.action?.requestHumanReviewProblem
    }
  }

  const selectedMedAlert = medAlerts.find((alert) => alert.id === medAlertId)

  if (!selectedMedAlert && !requestHumanReviewProblem) {
    return null
  }

  return (
    <div className="c-flex c-flex-col c-gap-2 c-mt-3">
      {selectedMedAlert && (
        <CurecallTag
          status={MED_ALERT_UI_TO_TAG_STATUS[selectedMedAlert.ui]}
          className="tw-w-auto tw-min-w-0"
        >
          {getTranslatedLabel(locale, selectedMedAlert.intlLabel)}
        </CurecallTag>
      )}

      {requestHumanReviewProblem && (
        <CurecallTag status="INFORMATION" className="tw-w-auto tw-min-w-0">
          {requestHumanReviewProblem !== EHumanReviewRequestProblem.UNKNOWN
            ? t('pages.configuration.followups.chatbot.conditional-sections.dashboard-tag-with-problem', {
                problem: problemLabels[requestHumanReviewProblem],
              })
            : t('pages.configuration.followups.chatbot.conditional-sections.display-in-dashboard')}
        </CurecallTag>
      )}
    </div>
  )
}
